import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  FEATURE_TRADE_TRANSFER_REASONS,
  REASON_FIELD_LABELS,
  TRADE_TRANSFER_KIND_CHOICES,
  TRADE_TRANSFER_KIND_LABELS,
  createTradeReason,
  fetchTradeReasonHistory,
  forgetTradeReasons,
  fetchTradeReasons,
  isTradeTransferKind,
  updateTradeReason,
  type TradeTransferKind,
  type TradeTransferReason,
  type TradeTransferReasonChange,
} from './tradeReasonsApi';

/**
 * „Powody przekazania do Trade” (makieta back office v2, A6: BR-FTL-03,
 * BR-TRD-11; przełącznik `tradeTransferReasons`, 20.10.2026).
 *
 * Dwie listy — przekazanie do Trade i zwrot z Trade do FTL. Wartości
 * startowe to wartości robocze z makiety, do potwierdzenia przez Marathon.
 * Administrator dodaje powód, zmienia treść i kolejność, zdejmuje z listy
 * (powodu się nie usuwa — jest w historii zleceń) i zaznacza, czy wymaga
 * komentarza. Każda zmiana z powodem, w historii poniżej.
 */

export const FEATURE_OFF_NOTE =
  'Ekran „Powody przekazania do Trade” jest wyłączony w wersji aplikacji, z której korzystasz.';

export const INTRO_NOTE =
  'Powód przy przekazaniu zlecenia do Trade i przy zwrocie z Trade do FTL wybiera się z tych list, a komentarz dopisuje obok. Listy to wartości robocze do potwierdzenia przez Marathon. Powodu nie usuwamy, bo jest w historii zleceń: zdejmij go z listy. Każda zmiana wymaga powodu i trafia do historii.';

type Editing =
  | {
      mode: 'create';
      kind: TradeTransferKind | '';
      label: string;
      requiresComment: boolean;
    }
  | {
      mode: 'update';
      row: TradeTransferReason;
      label: string;
      active: boolean;
      requiresComment: boolean;
      sortOrder: string;
    };

export const TradeReasonList = () => {
  const on = useFeatureState(FEATURE_TRADE_TRANSFER_REASONS);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('trade-transfer-reasons')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <TradeReasonScreen />;
};

/** Zmiana w oknie — tylko pola, które się różnią; `null` = nic do zapisu. */
export function updateInput(
  e: Extract<Editing, { mode: 'update' }>,
): { label?: string; active?: boolean; requiresComment?: boolean; sortOrder?: number } | null {
  const out: { label?: string; active?: boolean; requiresComment?: boolean; sortOrder?: number } = {};
  if (e.label.trim() !== e.row.label) out.label = e.label.trim();
  if (e.active !== e.row.active) out.active = e.active;
  if (e.requiresComment !== e.row.requiresComment) out.requiresComment = e.requiresComment;
  const order = Number(e.sortOrder);
  if (e.sortOrder.trim() !== '' && Number.isInteger(order) && order !== e.row.sortOrder) {
    out.sortOrder = order;
  }
  return Object.keys(out).length > 0 ? out : null;
}

const ReasonTable = ({
  kind,
  rows,
  onEdit,
}: {
  kind: TradeTransferKind;
  rows: TradeTransferReason[];
  onEdit: (row: TradeTransferReason) => void;
}) => {
  const own = rows
    .filter((r) => r.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  return (
    <Table size="small" aria-label={TRADE_TRANSFER_KIND_LABELS[kind]} data-testid={`reasons-${kind}`}>
      <TableHead>
        <TableRow>
          <TableCell>Powód</TableCell>
          <TableCell>Komentarz</TableCell>
          <TableCell align="right">Kolejność</TableCell>
          <TableCell>Ostatnia zmiana</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {own.map((r) => (
          <TableRow key={r.id} data-testid={`reason-${r.code}`}>
            <TableCell>
              {r.label}
              {r.active ? null : (
                <Chip size="small" variant="outlined" label="zdjęty z listy" sx={{ ml: 1 }} />
              )}
            </TableCell>
            <TableCell>{r.requiresComment ? 'wymagany' : 'opcjonalny'}</TableCell>
            <TableCell align="right">{r.sortOrder}</TableCell>
            <TableCell>
              {r.updatedAt ? fmtDate(r.updatedAt) : '—'}
              {r.updatedByName ? ` · ${r.updatedByName}` : ''}
            </TableCell>
            <TableCell align="right">
              <Button size="small" onClick={() => onEdit(r)}>
                Zmień
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const TradeReasonScreen = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<TradeTransferReason[] | null>(null);
  const [history, setHistory] = useState<TradeTransferReasonChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      forgetTradeReasons();
      const [r, h] = await Promise.all([fetchTradeReasons(), fetchTradeReasonHistory()]);
      setRows(r);
      setHistory(h);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (e: Editing) => {
    setReason('');
    setEditing(e);
  };

  const pending =
    editing?.mode === 'update' ? updateInput(editing) : null;
  const canSave =
    !!editing &&
    reason.trim() !== '' &&
    (editing.mode === 'create'
      ? editing.kind !== '' && editing.label.trim() !== ''
      : pending !== null);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.mode === 'create') {
        await createTradeReason({
          kind: editing.kind as TradeTransferKind,
          label: editing.label.trim(),
          requiresComment: editing.requiresComment,
          reason,
        });
      } else if (pending) {
        await updateTradeReason(editing.row.id, { ...pending, reason });
      }
      notify('Zapisano', { type: 'success' });
      setEditing(null);
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const edit = (row: TradeTransferReason) =>
    open({
      mode: 'update',
      row,
      label: row.label,
      active: row.active,
      requiresComment: row.requiresComment,
      sortOrder: String(row.sortOrder),
    });

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('trade-transfer-reasons')} />
      <Alert severity="info" sx={{ mb: 2 }} data-testid="trade-reasons-intro">
        {INTRO_NOTE}
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="outlined"
          onClick={() => open({ mode: 'create', kind: '', label: '', requiresComment: false })}
        >
          Dodaj powód
        </Button>
      </Stack>
      {rows === null && !error ? (
        <CircularProgress size={24} />
      ) : (
        (['handover', 'return'] as const).map((kind) => (
          <Box key={kind} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {TRADE_TRANSFER_KIND_LABELS[kind]}
            </Typography>
            <ReasonTable kind={kind} rows={rows ?? []} onEdit={edit} />
          </Box>
        ))
      )}

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Historia zmian
      </Typography>
      {(history ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Bez zmian.
        </Typography>
      ) : (
        <Table size="small" aria-label="Historia zmian">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Kto</TableCell>
              <TableCell>Powód na liście</TableCell>
              <TableCell>Zmiana</TableCell>
              <TableCell>Powód zmiany</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(history ?? []).map((h) => (
              <TableRow key={h.id}>
                <TableCell>{fmtDate(h.changedAt)}</TableCell>
                <TableCell>{h.changedByName ?? '—'}</TableCell>
                <TableCell>{h.code}</TableCell>
                <TableCell>
                  {REASON_FIELD_LABELS[h.field] ?? h.field}:{' '}
                  {h.field === 'created' ? h.newValue : `${h.oldValue ?? '—'} → ${h.newValue ?? '—'}`}
                </TableCell>
                <TableCell>{h.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing?.mode === 'create'
            ? 'Dodaj powód'
            : editing?.mode === 'update'
              ? `Zmień: ${editing.row.label}`
              : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editing?.mode === 'create' && (
              <TextField
                select
                label="Dotyczy"
                value={editing.kind}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    kind: isTradeTransferKind(e.target.value) ? e.target.value : '',
                  })
                }
              >
                {TRADE_TRANSFER_KIND_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {editing && (
              <TextField
                label="Treść powodu"
                value={editing.label}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
            )}
            {editing && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editing.requiresComment}
                    onChange={(e) => setEditing({ ...editing, requiresComment: e.target.checked })}
                  />
                }
                label="Wymaga komentarza (np. „inny powód”)"
              />
            )}
            {editing?.mode === 'update' && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    />
                  }
                  label="Na liście do wyboru"
                />
                <TextField
                  label="Kolejność"
                  type="number"
                  value={editing.sortOrder}
                  onChange={(e) => setEditing({ ...editing, sortOrder: e.target.value })}
                  slotProps={{ htmlInput: { min: 0, max: 9999 } }}
                />
              </>
            )}
            <TextField
              label="Powód zmiany"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Anuluj</Button>
          <Button variant="contained" disabled={!canSave || saving} onClick={() => void save()}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
